<template>
  <div
    v-click-outside="handleClickOutside"
    class="availability-selector-wrapper"
  >
    <!-- Activator slot for the badge -->
    <div
      ref="activator"
      class="availability-selector-activator"
      role="button"
      tabindex="0"
      @click="handleActivatorClick"
      @keydown.enter.prevent="handleActivatorClick"
      @keydown.space.prevent="handleActivatorClick"
    >
      <slot name="activator"></slot>
    </div>

    <!-- Popover content -->
    <div
      v-if="isOpen"
      ref="popover"
      class="availability-selector-popover"
      :style="popoverStyle"
      @keydown.esc="handleClose"
    >
      <!-- Caret -->
      <div class="availability-selector-caret"></div>

      <!-- Panel -->
      <div class="availability-selector-panel">
        <!-- Header -->
        <div class="availability-selector-header">
          <span class="availability-selector-title">Availability</span>
          <button
            type="button"
            class="availability-selector-close-btn"
            @click="handleClose"
            aria-label="Close"
          >
            <v-icon color="#98A2B3">mdi-close</v-icon>
          </button>
        </div>

        <!-- Locked state content -->
        <div
          v-if="locked"
          class="availability-selector-locked-notice"
        >
          <div class="availability-selector-notice-frame">
            <v-icon class="availability-selector-lock-icon" size="20">mdi-lock</v-icon>
            <div class="availability-selector-notice-message">
              <div class="availability-selector-notice-heading">Assigned to a primary position</div>
              <div class="availability-selector-notice-text">Availability can't be changed while this employee holds a primary position.</div>
            </div>
          </div>
        </div>

        <!-- Options (when not locked) -->
        <div
          v-else
          class="availability-selector-options"
          role="radiogroup"
          aria-label="Availability options"
        >
          <!-- 1 FTE option -->
          <div
            ref="option1"
            class="availability-selector-option"
            :class="{ 'availability-selector-option--selected': value === 1 }"
            role="radio"
            :aria-checked="value === 1"
            tabindex="0"
            @click="selectOption(1)"
            @keydown.enter.prevent="selectOption(1)"
            @keydown.space.prevent="selectOption(1)"
            @keydown.down.prevent="focusNextOption"
          >
            <div class="availability-selector-radio" :class="{ 'availability-selector-radio--selected': value === 1 }"></div>
            <EmployeeAvailabilityIcon :value="1" :isAllocated="false" />
            <div class="availability-selector-option-text">
              <span class="availability-selector-option-label" :class="{ 'availability-selector-option-label--selected': value === 1 }">1 FTE</span>
              <span class="availability-selector-option-sublabel">Full time</span>
            </div>
          </div>

          <!-- 0.5 FTE option -->
          <div
            ref="option05"
            class="availability-selector-option"
            :class="{ 'availability-selector-option--selected': value === 0.5 }"
            role="radio"
            :aria-checked="value === 0.5"
            tabindex="0"
            @click="selectOption(0.5)"
            @keydown.enter.prevent="selectOption(0.5)"
            @keydown.space.prevent="selectOption(0.5)"
            @keydown.up.prevent="focusPreviousOption"
            @keydown.down.prevent="focusFirstOption"
          >
            <div class="availability-selector-radio" :class="{ 'availability-selector-radio--selected': value === 0.5 }"></div>
            <EmployeeAvailabilityIcon :value="0.5" :isAllocated="false" />
            <div class="availability-selector-option-text">
              <span class="availability-selector-option-label" :class="{ 'availability-selector-option-label--selected': value === 0.5 }">0.5 FTE</span>
              <span class="availability-selector-option-sublabel">Part time</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import EmployeeAvailabilityIcon from '@components/organizations/employees/EmployeeAvailabilityIcon.vue';

const props = defineProps({
  value: {
    type: Number,
    default: 1,
    validator: (val) => [0, 0.5, 1].includes(val)
  },
  locked: {
    type: Boolean,
    default: false
  },
  open: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['input', 'close']);

// Refs
const activator = ref(null);
const popover = ref(null);
const option1 = ref(null);
const option05 = ref(null);

// State
const isOpen = ref(props.open);
const popoverStyle = ref({});

// Watch for open prop changes
watch(() => props.open, (newVal) => {
  isOpen.value = newVal;
  if (newVal) {
    nextTick(() => {
      calculatePosition();
    });
  }
});

// Methods
const handleActivatorClick = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      calculatePosition();
    });
  }
};

const handleClose = () => {
  isOpen.value = false;
  emit('close');
};

const handleClickOutside = (event) => {
  if (isOpen.value) {
    handleClose();
  }
};

const selectOption = (newValue) => {
  emit('input', newValue);
  handleClose();
};

const focusNextOption = () => {
  if (option05.value) {
    option05.value.focus();
  }
};

const focusPreviousOption = () => {
  if (option1.value) {
    option1.value.focus();
  }
};

const focusFirstOption = () => {
  if (option1.value) {
    option1.value.focus();
  }
};

const calculatePosition = () => {
  if (!activator.value || !popover.value) return;

  const activatorRect = activator.value.getBoundingClientRect();
  const popoverRect = popover.value.getBoundingClientRect();
  const caretWidth = 36; // From figma.md

  // Center the caret on the badge
  // Calculate: activator center - caret center
  const activatorCenter = activatorRect.left + (activatorRect.width / 2);
  const left = activatorCenter - 20; // Caret is padded 20px from left per figma

  // Position below the activator
  const top = activatorRect.bottom + 8;

  popoverStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    zIndex: 9999
  };
};

// Recalculate position on scroll
const handleScroll = () => {
  if (isOpen.value) {
    calculatePosition();
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleScroll);
});
</script>

<style scoped>
.availability-selector-wrapper {
  position: relative;
  display: inline-block;
}

.availability-selector-activator {
  cursor: pointer;
}

.availability-selector-activator:focus {
  outline: none;
}

.availability-selector-popover {
  position: fixed;
  z-index: 9999;
}

.availability-selector-caret {
  width: 36px;
  height: 8px;
  padding-left: 20px;
  display: flex;
  align-items: flex-end;
}

.availability-selector-caret::after {
  content: '';
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #eaecf0;
  margin-bottom: -1px;
}

.availability-selector-panel {
  width: 264px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08);
  margin-top: -1px;
}

.availability-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px 2px 16px;
  min-height: 46px;
}

.availability-selector-title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}

.availability-selector-close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.availability-selector-close-btn:hover {
  background-color: #f9fafb;
}

.availability-selector-close-btn:focus {
  outline: 2px solid #7f56d9;
  outline-offset: 0;
}

.availability-selector-locked-notice {
  padding: 4px 8px 8px 8px;
}

.availability-selector-notice-frame {
  display: flex;
  gap: 8px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
}

.availability-selector-lock-icon {
  flex-shrink: 0;
  color: #475467;
}

.availability-selector-notice-message {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.availability-selector-notice-heading {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.availability-selector-notice-text {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.availability-selector-options {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 8px 8px 8px;
}

.availability-selector-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.availability-selector-option:hover {
  background-color: #f9fafb;
}

.availability-selector-option:focus {
  outline: 2px solid #7f56d9;
  outline-offset: 0;
}

.availability-selector-option--selected {
  background-color: #f9fafb;
}

.availability-selector-radio {
  width: 16px;
  height: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 9999px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.availability-selector-radio--selected {
  background-color: #7f56d9;
  border-color: #7f56d9;
}

.availability-selector-radio--selected::after {
  content: '';
  width: 6px;
  height: 6px;
  background-color: #ffffff;
  border-radius: 9999px;
}

.availability-selector-option-text {
  display: flex;
  gap: 6px;
  align-items: center;
}

.availability-selector-option-label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.availability-selector-option-label--selected {
  color: #101828;
}

.availability-selector-option-sublabel {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}
</style>
